import { useState } from 'react'

import { t } from '../../../../../../../common/i18n.js'
import {
    resolveTimeOffCancelAccess,
    resolveTimeOffEditAccess,
    resolveTimeOffEditOutcome,
} from '../../../../../../../common/time-off-edit-rules.js'
import { Language } from '../../../../../../../common/types/i18n.js'
import { Button } from '../../../../../../components/button/button.js'
import {
    CalendarPlus,
    ChevronDown,
    Palmtree,
} from '../../../../../../components/icon/solar-icons.js'
import { PortalBookTimeAwayModal } from '../../../../../../components/portal-book-time-away-modal/portal-book-time-away-modal.js'
import { PortalEmptyState } from '../../../../../../components/portal-empty-state/portal-empty-state.js'
import { Section, SectionHeader } from '../../../../../../components/section/section.js'
import { todayStr } from '../../../../utils/format.js'
import type {
    LeaveRequest,
    LeaveSettlement,
    ProfileLeaveBalance,
    RequestConfirmFn,
} from '../../employee-profile-types.js'
import { LedgerPanel } from '../../ledger-panel.js'
import { TimeAwayAdjustModal } from './time-away-adjust-modal.js'
import { TimeAwayDaysTakenCard } from './time-away-days-taken-card.js'
import {
    buildCarryoverRows,
    buildDaysTakenRows,
    buildDetailsData,
    groupApprovedByYear,
    nextUpcomingHoliday,
    splitUpcoming,
    vacationStatBreakdown,
} from './time-away-derive.js'
import { TimeAwayDetailsCard } from './time-away-details-card.js'
import { TimeAwayPolicyModal } from './time-away-policy-modal.js'
import { TimeAwayRequestList } from './time-away-request-list.js'
import { TimeAwaySettlementCard } from './time-away-settlement-card.js'
import { TimeAwayStatsCard } from './time-away-stats-card.js'
import { TimeAwayYearCard } from './time-away-year-card.js'
import './time-away.css'

interface TimeAwayTabProps {
    employeeId: number
    leaveBalances: ProfileLeaveBalance[]
    leaveRequests: LeaveRequest[]
    /** Admin-only TLS §69/§71 settlement preview; null/undefined hides the card */
    leaveSettlement?: LeaveSettlement | null
    companyHolidays?: { date: string; name: string }[]
    isSelf: boolean
    isAdmin: boolean
    // Server-resolved time-away manage capability for this target (approve,
    // edit/book on behalf). Admin ledger/settlement stay gated on isAdmin.
    canManageTimeAway: boolean
    // Holder of the time-away settings key — may change the leave-policy
    // bundle from the details card (TASK-348).
    canManageLeavePolicies?: boolean
    lang: Language
    onNavigate?: (path: string) => void
    onBookTimeAway?: () => void
    requestConfirm?: RequestConfirmFn
    onCancelTimeAway?: (requestId: number) => Promise<boolean>
    onEditTimeAway?: (
        requestId: number,
        data: { start_date: string; end_date: string; note: string; policy_id: number },
    ) => Promise<boolean>
    onDecideTimeAway?: (
        requestId: number,
        action: 'approve' | 'reject',
        rejectionReason?: string,
    ) => Promise<boolean>
    // Approve/reject an owner's retroactive deletion request (TASK-402).
    onDecideCancellation?: (requestId: number, action: 'approve' | 'reject') => Promise<boolean>
    // Refetch leave data after the policy bundle changed (TASK-348) or an
    // admin corrected a balance (TASK-567).
    onLeaveDataChanged?: () => void
}

// Humaans-style layout: stats + details + days-taken card grid up top, then
// the upcoming list, the current-year history (with the carry-over row) and
// older years behind "Näita ajalugu". All derivation lives in
// time-away-derive.ts; this component only wires cards together.
export const TimeAwayTab = ({
    employeeId,
    leaveBalances,
    leaveRequests,
    leaveSettlement,
    companyHolidays,
    isSelf,
    isAdmin,
    canManageTimeAway,
    canManageLeavePolicies,
    lang,
    onBookTimeAway,
    requestConfirm,
    onCancelTimeAway,
    onEditTimeAway,
    onDecideTimeAway,
    onDecideCancellation,
    onLeaveDataChanged,
}: TimeAwayTabProps) => {
    const ph = t.portal
    const tOff = t.portal.timeOff
    const view = tOff.view

    const [showHistory, setShowHistory] = useState(false)
    const [ledgerOpen, setLedgerOpen] = useState(false)
    // The request being edited — editing opens the booking modal pre-filled,
    // so overlapping absences stay visible while changing dates.
    const [editingRequest, setEditingRequest] = useState<LeaveRequest | null>(null)
    // Leave-policy bundle editor (TASK-348), opened from the details card.
    const [policyModalOpen, setPolicyModalOpen] = useState(false)
    const [adjustModalOpen, setAdjustModalOpen] = useState(false)

    const today = todayStr()
    const currentYear = Number(today.slice(0, 4))

    const statBreakdown = vacationStatBreakdown(leaveBalances, leaveRequests, today)
    const details = buildDetailsData(leaveBalances)
    const nextHoliday = nextUpcomingHoliday(companyHolidays ?? [], today)
    const daysTaken = buildDaysTakenRows(leaveBalances, lang, leaveRequests, currentYear)
    const upcoming = splitUpcoming(leaveRequests, today)
    const yearGroups = groupApprovedByYear(
        leaveRequests,
        today,
        (companyHolidays ?? []).map((h) => h.date),
    )
    const carryoverRows = buildCarryoverRows(leaveBalances, currentYear)

    const currentYearGroup = yearGroups.find((g) => g.year === currentYear)
    const olderGroups = yearGroups.filter((g) => g.year !== currentYear)

    const onCancelRequest = (requestId: number) => {
        if (!onCancelTimeAway) return
        // An owner deleting a taken approval-flow absence doesn't cancel it yet
        // — the request goes to the manager. Same shared rules as the server;
        // the confirm copy tells the two flows apart.
        const req = leaveRequests.find((r) => r.id === requestId)
        const access = req
            ? resolveTimeOffCancelAccess({
                  isSelf,
                  canManageTarget: canManageTimeAway && !isSelf,
                  status: req.status,
                  startDate: req.start_date,
                  today,
                  requiresApproval: req.requiresApproval,
                  cancelRequested: req.cancel_requested_at_utc !== null,
              })
            : undefined
        const goesForApproval = access?.allowed === true && access.mode === 'requestApproval'
        if (requestConfirm) {
            requestConfirm(
                goesForApproval ? tOff.cancelRequestConfirm(lang) : tOff.cancelConfirm(lang),
                () => {
                    void onCancelTimeAway(requestId)
                },
                goesForApproval
                    ? // Not destructive yet — sending for approval, not deleting.
                      { confirmLabel: tOff.editReapprovalSubmit(lang), variant: 'primary' }
                    : undefined,
            )
        } else {
            void onCancelTimeAway(requestId)
        }
    }

    // Editing an own approved approval-flow request sends it back to the
    // manager — same shared rules as the server, surfaced as a confirm.
    // Called by the edit modal on submit.
    const submitEditRequest = (
        requestId: number,
        data: { start_date: string; end_date: string; note: string; policy_id: number },
    ) => {
        if (!onEditTimeAway) return
        const req = leaveRequests.find((r) => r.id === requestId)
        const access = req
            ? resolveTimeOffEditAccess({
                  isSelf,
                  canManageTarget: canManageTimeAway && !isSelf,
                  status: req.status,
                  endDate: req.end_date,
                  today,
              })
            : undefined
        const policyChanged = req !== undefined && data.policy_id !== req.policyId
        // Re-approval keys off the NEW policy when reclassifying — fall back to
        // the request's own policy for a dates/note-only edit.
        const requiresApproval = policyChanged
            ? (leaveBalances.find((b) => b.policyId === data.policy_id)?.requiresApproval ??
              req.requiresApproval)
            : (req?.requiresApproval ?? false)
        const needsReapproval =
            req !== undefined &&
            access?.allowed === true &&
            resolveTimeOffEditOutcome({
                editorRole: access.editorRole,
                // The edit button only renders on PENDING|APPROVED rows.
                status: req.status as 'PENDING' | 'APPROVED',
                requiresApproval,
                datesChanged: data.start_date !== req.start_date || data.end_date !== req.end_date,
                policyChanged,
            }) === 'revertToPending'
        if (needsReapproval && requestConfirm) {
            requestConfirm(
                tOff.editReapprovalConfirm(lang),
                () => {
                    void onEditTimeAway(requestId, data)
                },
                // Not destructive — sending for approval, not deleting.
                { confirmLabel: tOff.editReapprovalSubmit(lang), variant: 'primary' },
            )
        } else {
            void onEditTimeAway(requestId, data)
        }
    }

    // Concluded rows in the year cards — editable and deletable by the person
    // (via the approval flow) and time-away managers (gated row-by-row in the
    // list, same shared rules as the server).
    const editProps = {
        isSelf,
        canManage: canManageTimeAway,
        today,
        onStartEdit: onEditTimeAway ? setEditingRequest : undefined,
        onCancelRequest: onCancelTimeAway ? onCancelRequest : undefined,
        onDecideCancellation,
    }

    return (
        <div>
            {/* The page title is rendered by MeLayout (/me) / the profile tab strip
                (/people/:id), so the tab content starts straight with the cards. */}

            {/* Top card grid: stats + details stacked left, days taken right */}
            {leaveBalances.length > 0 && (
                <div className="time-away__grid">
                    <div className="time-away__col">
                        {statBreakdown && (
                            <TimeAwayStatsCard
                                breakdown={statBreakdown}
                                year={currentYear}
                                lang={lang}
                                onAdjust={isAdmin ? () => setAdjustModalOpen(true) : undefined}
                            />
                        )}
                        <TimeAwayDetailsCard
                            details={details}
                            nextHoliday={nextHoliday}
                            lang={lang}
                            onEditPolicy={
                                canManageLeavePolicies ? () => setPolicyModalOpen(true) : undefined
                            }
                        />
                        {isAdmin && leaveSettlement && (
                            <TimeAwaySettlementCard settlement={leaveSettlement} lang={lang} />
                        )}
                    </div>
                    <TimeAwayDaysTakenCard
                        visible={daysTaken.visible}
                        collapsed={daysTaken.collapsed}
                        lang={lang}
                    />
                </div>
            )}

            {/* Upcoming requests */}
            <Section>
                <SectionHeader
                    icon={Palmtree}
                    title={tOff.upcomingTitle(lang)}
                    actions={
                        onBookTimeAway && (isSelf || canManageTimeAway) ? (
                            <Button
                                variant="primary"
                                size="small"
                                iconLeft={CalendarPlus}
                                text={
                                    isSelf
                                        ? ph.profile.bookTimeAway(lang)
                                        : ph.profile.addTimeAway(lang)
                                }
                                onClick={onBookTimeAway}
                            />
                        ) : undefined
                    }
                />
                {upcoming.length === 0 ? (
                    <PortalEmptyState icon="🏖️" message={ph.profile.noTimeOff(lang)} />
                ) : (
                    <TimeAwayRequestList
                        requests={upcoming}
                        variant="upcoming"
                        lang={lang}
                        isSelf={isSelf}
                        canManage={canManageTimeAway}
                        today={today}
                        onCancelRequest={onCancelTimeAway ? onCancelRequest : undefined}
                        onStartEdit={onEditTimeAway ? setEditingRequest : undefined}
                        onDecideRequest={onDecideTimeAway}
                        onDecideCancellation={onDecideCancellation}
                    />
                )}
            </Section>

            {/* Current year so far, incl. the highlighted carry-over row */}
            {(currentYearGroup !== undefined || carryoverRows.length > 0) && (
                <TimeAwayYearCard
                    title={view.currentYearTitle(currentYear)(lang)}
                    requests={currentYearGroup?.requests ?? []}
                    carryoverRows={carryoverRows}
                    lang={lang}
                    editProps={editProps}
                />
            )}

            {/* Older years, revealed on demand */}
            {olderGroups.length > 0 && !showHistory && (
                <div className="time-away__history">
                    <button
                        type="button"
                        className="time-away__expander"
                        onClick={() => setShowHistory(true)}
                    >
                        {view.showHistory(lang)}
                        <ChevronDown size={14} />
                    </button>
                </div>
            )}
            {showHistory &&
                olderGroups.map((group) => (
                    <TimeAwayYearCard
                        key={group.year}
                        title={String(group.year)}
                        requests={group.requests}
                        lang={lang}
                        editProps={editProps}
                    />
                ))}

            {/* Admin-only read-only ledger history (TASK-567: corrections live
                in the stats-card modal; this is the audit trail) */}
            {isAdmin && (
                <LedgerPanel
                    employeeId={employeeId}
                    policies={leaveBalances.map((b) => ({
                        policyId: b.policyId,
                        policyName: b.policyName,
                        leaveTypeName: b.leaveTypeName,
                    }))}
                    lang={lang}
                    open={ledgerOpen}
                    onOpenChange={setLedgerOpen}
                />
            )}

            {/* Admin balance correction as of a date (TASK-567) — only limited
                types (the settings "Limiidiga" rule set) carry a balance. */}
            {isAdmin && (
                <TimeAwayAdjustModal
                    isOpen={adjustModalOpen}
                    onClose={() => setAdjustModalOpen(false)}
                    employeeId={employeeId}
                    policies={leaveBalances
                        .filter((b) => b.accrualMethod !== 'none')
                        .map((b) => ({
                            policyId: b.policyId,
                            policyName: b.policyName,
                            leaveTypeName: b.leaveTypeName,
                        }))}
                    lang={lang}
                    onSaved={() => onLeaveDataChanged?.()}
                />
            )}

            {/* Leave-policy bundle editor (TASK-348) */}
            {canManageLeavePolicies && (
                <TimeAwayPolicyModal
                    isOpen={policyModalOpen}
                    onClose={() => setPolicyModalOpen(false)}
                    employeeId={employeeId}
                    lang={lang}
                    onSaved={() => onLeaveDataChanged?.()}
                />
            )}

            {/* Edit modal — the booking flow pre-filled with the request being edited */}
            <PortalBookTimeAwayModal
                isOpen={editingRequest !== null}
                onClose={() => setEditingRequest(null)}
                myEmployeeId={employeeId}
                lang={lang}
                editRequest={editingRequest}
                onSubmitEdit={(data) => {
                    if (editingRequest) submitEditRequest(editingRequest.id, data)
                }}
            />
        </div>
    )
}
